/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import user from 'js/lib/user';

import { Breadcrumbs, Grid, Link, PageGridContainer, Typography2 } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import useHasMultipleProgramProps from 'bundles/program-home/components/multiprogram/useHasMultipleProgramProps';
import {
  generateSkillsPageUrl,
  generateSkillsetSkillsPageUrl,
} from 'bundles/skills-common/private/utils/skillLinkUtils';
import { TabName } from 'bundles/unified-common/constants/enterprise';
import Skeleton from 'bundles/unified-home-common/components/shared/Skeleton';

import _t from 'i18n!nls/program-skills-objectives';

type PropsFromCaller = {
  programSlug: string;
  thirdPartyOrgSlug: string;
  thirdPartyOrganizationId: string;
  programName: string;
  objectiveSlug?: string;
  objectiveName?: string;
  skillId?: string;
  skillName?: string;
  invertColor?: boolean;
  showSkillDashboard?: boolean;
};

type PropsForPlaceholder = Pick<
  PropsFromCaller,
  'programSlug' | 'objectiveSlug' | 'thirdPartyOrgSlug' | 'thirdPartyOrganizationId' | 'programName' | 'invertColor'
>;

type Props = Omit<PropsFromCaller, 'objectiveName'> & {
  additionalElements: BreadcrumbItemElement[];
};

type BreadcrumbItemElement = {
  key: string;
  element: React.ReactNode;
};

type PropsForHomeLink = {
  invertColor?: boolean;
  objectiveSlug?: string;
  programSlug: string;
  thirdPartyOrgSlug: string;
};

export const MultiProgramHomeLink = ({
  invertColor,
  programSlug,
  objectiveSlug,
  thirdPartyOrgSlug,
  ...breadcrumbItemProps
}: PropsForHomeLink) => (
  <Breadcrumbs.Item {...breadcrumbItemProps}>
    <Link
      invert={invertColor}
      variant="quiet"
      data={{ programSlug, skillsetSlug: objectiveSlug }}
      trackingName="skillset_org_home_breadcrumb_link"
      component={TrackedLink2}
      href={`/programs/all/${thirdPartyOrgSlug}`}
      css={css`
        font-weight: 700;
      `}
    >
      {_t('All Learning Programs')}
    </Link>
  </Breadcrumbs.Item>
);

export const ObjectivesBreadcrumbFC = ({
  programSlug,
  objectiveSlug,
  additionalElements,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  invertColor,
  programName,
}: Props) => {
  const { hasMultipleProgramsInOrg } = useHasMultipleProgramProps({ userId: user.get().id, thirdPartyOrganizationId });
  const homeUrl = hasMultipleProgramsInOrg ? `/programs/all/${thirdPartyOrgSlug}` : `/programs/${programSlug}`;

  const styles = {
    gridContainer: css`
      padding-left: 0 !important; /* override CDS padding */
    `,
    container: css`
      margin: var(--cds-spacing-300) 0;

      > div {
        padding-left: 0 !important; /* override CDS padding */
      }

      .cds-breadcrumbs-list {
        align-items: center;
      }
    `,
    itemText: css`
      font-weight: 700;
    `,
  };

  return (
    <PageGridContainer css={styles.gridContainer}>
      <Grid item xs={12}>
        <Typography2
          css={styles.container}
          variant="subtitleMedium"
          component="div"
          color={invertColor ? 'invertBody' : undefined}
        >
          <Breadcrumbs invert={invertColor}>
            <Breadcrumbs.Item href={homeUrl}>{_t('Home')}</Breadcrumbs.Item>
            {hasMultipleProgramsInOrg && (
              <MultiProgramHomeLink
                invertColor={invertColor}
                programSlug={programSlug}
                objectiveSlug={objectiveSlug}
                thirdPartyOrgSlug={thirdPartyOrgSlug}
              />
            )}
            <Breadcrumbs.Item>
              <Link
                invert={invertColor}
                variant="quiet"
                data={{ programSlug, skillsetSlug: objectiveSlug }}
                trackingName="skillset_program_home_breadcrumb_link"
                component={TrackedLink2}
                href={`/programs/${programSlug}`}
                css={styles.itemText}
              >
                {programName}
              </Link>
            </Breadcrumbs.Item>
            {!!objectiveSlug && (
              <Breadcrumbs.Item>
                <Link
                  invert={invertColor}
                  variant="quiet"
                  data={{ programSlug, skillsetSlug: objectiveSlug }}
                  trackingName="skillset_skillsets_breadcrumb_link"
                  component={TrackedLink2}
                  href={`/programs/${programSlug}?currentTab=${TabName.SkillSets}`}
                  css={styles.itemText}
                >
                  {_t('SkillSets')}
                </Link>
              </Breadcrumbs.Item>
            )}
            {additionalElements.map((additionalElement) => (
              <Breadcrumbs.Item key={additionalElement.key} css={styles.itemText}>
                {additionalElement.element}
              </Breadcrumbs.Item>
            ))}
          </Breadcrumbs>
        </Typography2>
      </Grid>
    </PageGridContainer>
  );
};

// Returns breadcrumb items based on if skillName is present
// If present: tracked link to objective page and skill name
// Not present: objective name
const getBreadcrumbItemElements = (
  programSlug: string,
  objectiveName?: string,
  objectiveSlug?: string,
  skillName?: string,
  skillId?: string,
  showSkillDashboard?: boolean,
  invertColor?: boolean
) => {
  const items = [];
  if (skillName) {
    if (objectiveSlug && objectiveName) {
      items.push({
        key: objectiveName,
        element: (
          <Link
            variant="quiet"
            trackingName="skillset_objectives_breadcrumb_link"
            component={TrackedLink2}
            href={`/programs/${programSlug}/skillsets/${objectiveSlug}/`}
          >
            {objectiveName}
          </Link>
        ),
      });
    }
    if (showSkillDashboard) {
      items.push({
        key: 'dashboard',
        element: (
          <Link
            variant="quiet"
            trackingName="skills_dashboard_breadcrumb_link"
            component={TrackedLink2}
            href={`/programs/${programSlug}?currentTab=MY_LEARNING&myLearningTab=SKILLS_DASHBOARD`}
          >
            {_t('Skills Dashboard')}
          </Link>
        ),
      });
    }
    if (skillId) {
      items.push({
        key: skillName,
        element: (
          <Link
            variant="quiet"
            trackingName="skill_breadcrumb_link"
            component={TrackedLink2}
            href={
              objectiveSlug
                ? generateSkillsetSkillsPageUrl(skillId, programSlug, objectiveSlug)
                : generateSkillsPageUrl(skillId, programSlug)
            }
          >
            {skillName}
          </Link>
        ),
      });
    } else {
      items.push({
        key: skillName,
        element: skillName,
      });
    }
  } else if (objectiveSlug && objectiveName) {
    items.push({
      key: objectiveName,
      element: (
        <Link
          variant="quiet"
          invert={invertColor}
          trackingName="skillset_objectives_breadcrumb_link"
          component={TrackedLink2}
          href={`/programs/${programSlug}/skillsets/${objectiveSlug}/`}
          aria-current="page"
        >
          {objectiveName}
        </Link>
      ),
    });
  }

  return items;
};

const ObjectivesBreadcrumb = ({
  programSlug,
  objectiveName,
  objectiveSlug,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  programName,
  invertColor,
  showSkillDashboard,
  skillId,
  skillName,
}: PropsFromCaller): JSX.Element => (
  <ObjectivesBreadcrumbFC
    programSlug={programSlug}
    invertColor={invertColor}
    objectiveSlug={objectiveSlug}
    thirdPartyOrgSlug={thirdPartyOrgSlug}
    thirdPartyOrganizationId={thirdPartyOrganizationId}
    programName={programName}
    additionalElements={getBreadcrumbItemElements(
      programSlug,
      objectiveName,
      objectiveSlug,
      skillName,
      skillId,
      showSkillDashboard,
      invertColor
    )}
  />
);

const ObjectivesBreadcrumbPlaceholder = ({
  programSlug,
  objectiveSlug,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  invertColor,
  programName,
}: PropsForPlaceholder): JSX.Element => (
  <ObjectivesBreadcrumbFC
    invertColor={invertColor}
    programSlug={programSlug}
    objectiveSlug={objectiveSlug}
    thirdPartyOrgSlug={thirdPartyOrgSlug}
    thirdPartyOrganizationId={thirdPartyOrganizationId}
    programName={programName}
    additionalElements={[
      {
        key: 'placeholder',
        element: <Skeleton fixedWidth={200} />,
      },
    ]}
  />
);

ObjectivesBreadcrumb.Placeholder = ObjectivesBreadcrumbPlaceholder;

export default ObjectivesBreadcrumb;
